/**
 * 共用 LaTeX 語法輔助元件
 * B.7.1 / B.7.2 / B.10~B.13 / FormulaConfig 共同使用
 * 底部「常用公式」區塊改為從 formula_configs API 動態載入
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';

// ── 語法分類資料 ──────────────────────────────────────────────────
const LATEX_GROUPS = [
  {
    label: { zh: '上下標', en: 'Sub/Superscript' },
    items: [
      { label: 'x 下標',  en: 'Subscript',   latex: 'x_{n}',           example: 'AD_{i,y}' },
      { label: 'x 上標',  en: 'Superscript', latex: 'x^{n}',           example: 'CO_2^{eq}' },
      { label: '上下標',  en: 'Both',        latex: 'x_{n}^{m}',       example: 'BE_{y}^{BAU}' },
    ],
  },
  {
    label: { zh: '運算符', en: 'Operators' },
    items: [
      { label: '乘 ×',    en: 'Times',      latex: '\\times',          example: 'a \\times b' },
      { label: '除 ÷',    en: 'Divide',     latex: '\\div',            example: 'a \\div b' },
      { label: '分數',     en: 'Fraction',   latex: '\\frac{a}{b}',    example: '\\frac{1}{2}' },
      { label: '平方根',   en: 'Sqrt',       latex: '\\sqrt{x}',       example: '\\sqrt{2}' },
    ],
  },
  {
    label: { zh: '求和/連乘', en: 'Sum/Product' },
    items: [
      { label: '求和 Σ',  en: 'Sum',        latex: '\\sum_{i=1}^{n}', example: '\\sum_{i}' },
      { label: '連乘 Π',  en: 'Product',    latex: '\\prod_{i=1}^{n}',example: '\\prod_{i}' },
      { label: '積分 ∫',  en: 'Integral',   latex: '\\int_{a}^{b}',   example: '\\int_0^T' },
    ],
  },
  {
    label: { zh: '括號', en: 'Brackets' },
    items: [
      { label: '自適應 ()', en: 'Paren',    latex: '\\left( \\right)', example: '\\left( x \\right)' },
      { label: '自適應 []', en: 'Bracket',  latex: '\\left[ \\right]', example: '\\left[ x \\right]' },
      { label: '自適應 {}', en: 'Brace',    latex: '\\left\\{ \\right\\}', example: '' },
    ],
  },
  {
    label: { zh: '希臘字母', en: 'Greek' },
    items: [
      { label: 'α alpha',  en: 'alpha',     latex: '\\alpha',          example: '' },
      { label: 'β beta',   en: 'beta',      latex: '\\beta',           example: '' },
      { label: 'γ gamma',  en: 'gamma',     latex: '\\gamma',          example: '' },
      { label: 'Δ Delta',  en: 'Delta',     latex: '\\Delta',          example: '' },
      { label: 'σ sigma',  en: 'sigma',     latex: '\\sigma',          example: '' },
      { label: 'μ mu',     en: 'mu',        latex: '\\mu',             example: '' },
      { label: 'η eta',    en: 'eta',       latex: '\\eta',            example: '' },
      { label: 'θ theta',  en: 'theta',     latex: '\\theta',          example: '' },
    ],
  },
  {
    label: { zh: '比較/特殊', en: 'Compare/Special' },
    items: [
      { label: '≤',        en: 'leq',       latex: '\\leq',            example: '' },
      { label: '≥',        en: 'geq',       latex: '\\geq',            example: '' },
      { label: '≠',        en: 'neq',       latex: '\\neq',            example: '' },
      { label: '≈',        en: 'approx',    latex: '\\approx',         example: '' },
      { label: '∞',        en: 'infty',     latex: '\\infty',          example: '' },
      { label: '純文字',    en: 'text',      latex: '\\text{文字}',     example: '\\text{tCO}_2' },
    ],
  },
];

// ── 元件介面 ──────────────────────────────────────────────────────
interface LatexHelperProps {
  isZh: boolean;
  onInsert: (snippet: string) => void;
}

// ── 元件 ──────────────────────────────────────────────────────────
const LatexHelper: React.FC<LatexHelperProps> = ({ isZh, onInsert }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  // 動態公式範本
  const [formulaTemplates, setFormulaTemplates] = useState<FormulaConfigItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplatesLoading(true);
    formulaConfigService.listActive()
      .then(setFormulaTemplates)
      .catch(() => setFormulaTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [open]);

  // 計算彈窗位置（fixed，避免被 overflow:hidden 截斷）
  const POPUP_W = 460;
  const POPUP_H = 480; // 估算高度，決定是否往上展開

  const calcPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // 水平：靠右對齊按鈕，但不超出視窗右邊
    const left = Math.max(8, Math.min(rect.right - POPUP_W, vw - POPUP_W - 8));

    // 垂直：優先往下，若不夠空間則往上
    const spaceBelow = vh - rect.bottom - 8;
    const top = spaceBelow >= POPUP_H
      ? rect.bottom + 4
      : Math.max(8, rect.top - POPUP_H - 4);

    setPopupStyle({ position: 'fixed', top, left, zIndex: 9999 });
  }, []);

  const handleToggle = () => {
    if (!open) calcPosition();
    setOpen(o => !o);
  };

  // 視窗縮放或捲動時重新定位
  useEffect(() => {
    if (!open) return;
    const update = () => calcPosition();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, calcPosition]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          padding: '4px 12px', fontSize: 12, borderRadius: 5,
          border: '1px solid #4A90E2',
          background: open ? '#4A90E2' : '#fff',
          color: open ? '#fff' : '#4A90E2',
          cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap',
        }}
      >
        {t('common.latexHelp')}
      </button>

      {open && (
        <div style={{
          ...popupStyle,
          width: 460, background: '#fff', borderRadius: 8,
          border: '1px solid #d0d0d0', boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
          {/* 標題列 */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{t('common.latexQuickRef')}</span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#888' }}>✕</button>
          </div>

          {/* 分類頁籤 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
            {LATEX_GROUPS.map((g, i) => (
              <button key={i} onClick={() => setActiveGroup(i)}
                style={{
                  padding: '3px 10px', fontSize: 11, borderRadius: 4, border: '1px solid',
                  borderColor: activeGroup === i ? '#4A90E2' : '#e0e0e0',
                  background: activeGroup === i ? '#4A90E2' : '#fafafa',
                  color: activeGroup === i ? '#fff' : '#555',
                  cursor: 'pointer',
                }}>
                {isZh ? g.label.zh : g.label.en}
              </button>
            ))}
          </div>

          {/* 語法列表 */}
          <div style={{ padding: '8px 12px', maxHeight: 220, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '4px 8px', textAlign: 'left', width: '25%' }}>{t('common.latexName')}</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left', width: '35%' }}>LaTeX</th>
                  <th style={{ padding: '4px 8px', textAlign: 'left', width: '25%' }}>{t('common.latexExample')}</th>
                  <th style={{ padding: '4px 8px', width: '15%' }}></th>
                </tr>
              </thead>
              <tbody>
                {LATEX_GROUPS[activeGroup].items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '5px 8px', color: '#555' }}>
                      {isZh ? item.label : item.en}
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      <code style={{ background: '#f0f0f0', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>
                        {item.latex}
                      </code>
                    </td>
                    <td style={{ padding: '5px 8px', color: '#888', fontSize: 11 }}>
                      {item.example && <code>{item.example}</code>}
                    </td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                      <button
                        onClick={() => { onInsert(item.latex); setOpen(false); }}
                        style={{
                          padding: '2px 8px', fontSize: 11, background: '#e8f4ff',
                          border: '1px solid #4A90E2', color: '#4A90E2', borderRadius: 3, cursor: 'pointer',
                        }}>
                        {t('common.latexInsert')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 常用公式（動態載入自 formula_configs） */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>
                {isZh ? '常用公式（點擊套用）' : 'Formula Templates (click to insert)'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 120, overflowY: 'auto' }}>
              {templatesLoading ? (
                <div style={{ fontSize: 11, color: '#aaa', padding: '4px 0' }}>
                  {isZh ? '載入中...' : 'Loading...'}
                </div>
              ) : formulaTemplates.length === 0 ? (
                <div style={{ fontSize: 11, color: '#bbb', padding: '4px 0', fontStyle: 'italic' }}>
                  {isZh ? '尚未設定公式，請至「方程式設定」維護' : 'No formulas configured. Go to "Formula Configuration" to add.'}
                </div>
              ) : (
                formulaTemplates.map(f => (
                  <button key={f.id}
                    onClick={() => { if (f.formula_latex) { onInsert(f.formula_latex); setOpen(false); } }}
                    disabled={!f.formula_latex}
                    style={{
                      padding: '4px 10px', fontSize: 11, textAlign: 'left',
                      background: '#fafffe', border: '1px solid #e0e0e0', borderRadius: 4,
                      cursor: f.formula_latex ? 'pointer' : 'default', color: '#333',
                    }}>
                    <span style={{ color: '#52c41a', marginRight: 6 }}>↩</span>
                    <strong>{f.formula_code}：</strong>
                    {isZh ? f.name_zh : f.name_en}
                    {f.formula_latex && (
                      <code style={{ fontSize: 10, color: '#555', marginLeft: 6 }}>
                        {f.formula_latex.length > 40 ? f.formula_latex.slice(0, 40) + '…' : f.formula_latex}
                      </code>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LatexHelper;
